// libraries.
import { Form, Row, Col } from "react-bootstrap";
import { UseFormRegister, FieldErrors, UseFormWatch } from "react-hook-form";

// styles.
import { StyledRulesModalForm } from "../../styles";

// types.
import { EditStepFormValues } from "./useEditStepForm";

interface Props {
  register: UseFormRegister<EditStepFormValues>;
  errors: FieldErrors<EditStepFormValues>;
  watch: UseFormWatch<EditStepFormValues>;
  hasDayRange: boolean;
  hasPercentage: boolean;
}

export const EditStepFields = ({
  register,
  errors,
  watch,
  hasDayRange,
  hasPercentage,
}: Props) => {
  const dayRangeFrom = watch("dayRangeFrom") ?? 0;

  return (
    <StyledRulesModalForm className="d-flex flex-column gap-3">

      {/* Nombre + Prioridad */}
      <Row className="g-3">
        <Col xs={9}>
          <Form.Group>
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Nombre del paso"
              {...register("name", { required: "Requerido" })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col xs={3}>
          <Form.Group>
            <Form.Label>Prioridad</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={0}
              {...register("priority", {
                required: "Requerido",
                valueAsNumber: true,
                min: { value: 0, message: "≥ 0" },
              })}
              isInvalid={!!errors.priority}
            />
            <Form.Control.Feedback type="invalid">
              {errors.priority?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Descripción */}
      <Form.Group>
        <Form.Label>Descripción</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          placeholder="Describe el comportamiento de este paso"
          {...register("description", { required: "Requerido" })}
          isInvalid={!!errors.description}
        />
        <Form.Control.Feedback type="invalid">
          {errors.description?.message}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Rango de días — solo si la regla tiene condiciones dias_full */}
      {hasDayRange && (
        <div>
          <Form.Label>Rango de días en Full</Form.Label>
          <Row className="g-2">
            <Col xs={6}>
              <Form.Group>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Día inicio"
                  min={0}
                  {...register("dayRangeFrom", {
                    required: "Requerido",
                    valueAsNumber: true,
                    min: { value: 0, message: "≥ 0" },
                  })}
                  isInvalid={!!errors.dayRangeFrom}
                />
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Día inicio (inclusive)
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.dayRangeFrom?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Sin límite"
                  min={0}
                  {...register("dayRangeTo", {
                    validate: (val) => {
                      if (val === "" || val === undefined) return true;
                      const num = Number(val);
                      if (isNaN(num)) return "Debe ser un número";
                      if (num <= dayRangeFrom) return `Debe ser > ${dayRangeFrom}`;
                      return true;
                    },
                  })}
                  isInvalid={!!errors.dayRangeTo}
                />
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Día fin (inclusive, vacío = sin límite)
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.dayRangeTo?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Porcentaje de ajuste — solo si la regla tiene acción descuentos/api_bonos */}
      {hasPercentage && (
        <Form.Group>
          <Form.Label>Porcentaje de ajuste (%)</Form.Label>
          <Form.Control
            type="number"
            size="sm"
            step="0.01"
            min={0}
            max={100}
            placeholder="Ej: 3"
            {...register("percentage", {
              required: "Requerido",
              valueAsNumber: true,
              min: { value: 0, message: "≥ 0" },
              max: { value: 100, message: "≤ 100" },
            })}
            isInvalid={!!errors.percentage}
          />
          <Form.Text className="text-muted" style={{ fontSize: 11 }}>
            Se aplica sobre todas las acciones de descuento/bono de esta regla
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            {errors.percentage?.message}
          </Form.Control.Feedback>
        </Form.Group>
      )}

      <Form.Group>
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0">Estado actual:</Form.Label>
          <span className={`badge-status ${watch("isActive") ? "active" : "inactive"}`}>
            {watch("isActive") ? "● Activa" : "○ Inactiva"}
          </span>
        </div>
        <Form.Text className="d-block text-muted mt-1" style={{ fontSize: 11 }}>
          Para cambiar el estado usá el botón Activar / Desactivar en la tabla.
        </Form.Text>
      </Form.Group>

    </StyledRulesModalForm>
  );
};
